import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Grid,
  Typography,
  Card,
  CardContent,
  useTheme,
  CircularProgress,
  Chip,
  Alert,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar
} from '@mui/material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  AreaChart,
  Area
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  Assessment,
  Security,
  AutoAwesome,
  ExpandMore,
  Psychology,
  MonetizationOn,
  Warning,
  CheckCircle,
  LocationOn,
  House
} from '@mui/icons-material';
import apiClient from '../../services/apiClient';
import aiAnalyticsService from '../../services/aiAnalyticsService';
import { formatCurrency } from './utils/formatters';
import styles from './styles/PropertyAnalytics.module.css';

const PropertyAnalytics = () => {
  const theme = useTheme();
  const [properties, setProperties] = useState([]);
  const [marketInsights, setMarketInsights] = useState(null);
  const [riskAssessment, setRiskAssessment] = useState(null);
  const [marketPrediction, setMarketPrediction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);

  const CHART_COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00ff00', '#0000ff'];

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      // Removed debug logging
      const response = await apiClient.get('/listings', { params: { limit: 10000 } });
      // Removed debug logging
      const propertiesData = response.data.data || response.data; // Handle both response structures
      // Removed debug logging
      setProperties(propertiesData);
    } catch (error) {
      console.error('❌ Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateAIInsights = async () => {
    if (!properties.length) return;
    
    try {
      setAiLoading(true);
      
      // Fetch AI insights concurrently
      const [insights, risk, prediction] = await Promise.all([
        aiAnalyticsService.getMarketInsights(properties),
        aiAnalyticsService.getRiskAssessment(properties),
        aiAnalyticsService.getMarketTrendPrediction(generateHistoricalData())
      ]);

      setMarketInsights(insights);
      setRiskAssessment(risk);
      setMarketPrediction(prediction);
    } catch (error) {
      console.error('Error generating AI insights:', error);
    } finally {
      setAiLoading(false);
    }
  };

  const generateHistoricalData = () => {
    // Generate sample historical data from current properties
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
    return months.map(month => ({
      month,
      avgPrice: Math.floor(Math.random() * 100000) + 200000,
      properties: Math.floor(Math.random() * 50) + 20
    }));
  };

  const getLocationDistribution = () => {
    const cityCount = {};
    properties.forEach(property => {
      const city = property.city || 'Unknown';
      cityCount[city] = (cityCount[city] || 0) + 1;
    });

    return Object.entries(cityCount)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 6)
      .map(([name, value]) => ({ name, value }));
  };

  const getCountyDistribution = () => {
    const countyCount = {};
    properties.forEach(property => {
      const county = (property.county || 'Unknown').replace(' County', '');
      countyCount[county] = (countyCount[county] || 0) + 1;
    });

    return Object.entries(countyCount)
      .sort(([,a], [,b]) => b - a)
      .map(([name, value]) => ({ name, value }));
  };

  const getStatusDistribution = () => {
    const statusCount = {};
    properties.forEach(property => {
      const status = property.status || 'Unknown';
      statusCount[status] = (statusCount[status] || 0) + 1;
    });

    return Object.entries(statusCount)
      .map(([name, value]) => ({ name, value }));
  };

  const getPriceDistribution = () => {
    const propertiesWithValues = properties.filter(p => p.upset_value && parseFloat(p.upset_value) > 0);
    
    const ranges = [
      { range: '<$100K', min: 0, max: 100000 },
      { range: '$100K-$300K', min: 100000, max: 300000 },
      { range: '$300K-$500K', min: 300000, max: 500000 },
      { range: '$500K-$1M', min: 500000, max: 1000000 },
      { range: '>$1M', min: 1000000, max: Infinity }
    ];

    return ranges.map(({ range, min, max }) => ({
      range,
      count: propertiesWithValues.filter(p => {
        const price = parseFloat(p.upset_value) || 0;
        return price >= min && price < max;
      }).length
    }));
  };

  const getSaleDateTrends = () => {
    const monthCount = {};
    const currentYear = new Date().getFullYear();
    
    properties.forEach(property => {
      if (property.sale_date) {
        const date = new Date(property.sale_date);
        if (date.getFullYear() >= currentYear) {
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          monthCount[monthKey] = (monthCount[monthKey] || 0) + 1;
        }
      }
    });

    return Object.entries(monthCount)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(0, 12)
      .map(([month, count]) => ({
        month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        properties: count
      }));
  };

  const getAttorneyDistribution = () => {
    const attorneyCount = {};
    properties.forEach(property => {
      const attorney = property.attorney || 'Unknown';
      attorneyCount[attorney] = (attorneyCount[attorney] || 0) + 1;
    });

    return Object.entries(attorneyCount)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 8)
      .map(([name, value]) => ({ name: name.length > 25 ? name.substring(0, 25) + '...' : name, value }));
  };

  const renderMarketInsights = () => {
    if (!marketInsights) return null;

    return (
      <Grid item xs={12}>
        <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', mr: 2 }}>
                <Psychology />
              </Avatar>
              <Typography variant="h6">AI Market Insights</Typography>
            </Box>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ mb: 1 }}>
                    {marketInsights.marketSentiment}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Market Sentiment
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ mb: 1 }}>
                    {marketInsights.avgPriceTrend}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Price Trend
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ mb: 1 }}>
                    {marketInsights.hotspots?.length || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Hot Markets
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Typography sx={{ mt: 2, opacity: 0.95 }}>
              {marketInsights.summary}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  const renderRiskAssessment = () => {
    if (!riskAssessment) return null;

    const riskData = [
      { subject: 'Market Risk', value: riskAssessment.riskBreakdown.market || 50 },
      { subject: 'Legal Risk', value: riskAssessment.riskBreakdown.legal || 40 },
      { subject: 'Financial Risk', value: riskAssessment.riskBreakdown.financial || 45 },
      { subject: 'Operational Risk', value: riskAssessment.riskBreakdown.operational || 35 }
    ];

    return (
      <Grid item xs={12} md={6}>
        <Paper 
          elevation={3}
          sx={{ 
            p: 3, 
            height: 400,
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
            border: '1px solid rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Box sx={{ 
              width: 6, 
              height: 28, 
              background: `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`,
              borderRadius: '3px',
              mr: 2 
            }} />
            <Security sx={{ mr: 1, color: theme.palette.error.main, fontSize: 24 }} />
            <Typography variant="h6" fontWeight="600">Risk Assessment</Typography>
          </Box>
          <ResponsiveContainer width="100%" height={260}>
            <RadarChart data={riskData}>
              <PolarGrid stroke="#e0e0e0" />
              <PolarAngleAxis 
                dataKey="subject" 
                tick={{ fontSize: 12, fill: '#666' }}
              />
              <PolarRadiusAxis 
                domain={[0, 100]} 
                tick={{ fontSize: 10, fill: '#999' }}
              />
              <Radar
                name="Risk Level"
                dataKey="value"
                stroke={theme.palette.error.main}
                fill={theme.palette.error.main}
                fillOpacity={0.2}
                strokeWidth={2}
              />
            </RadarChart>
          </ResponsiveContainer>
          <Box sx={{ 
            mt: 2, 
            p: 2, 
            background: `linear-gradient(135deg, ${theme.palette.error.main}15 0%, ${theme.palette.error.main}08 100%)`,
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <Typography variant="h5" fontWeight="700" color={theme.palette.error.main}>
              {riskAssessment.portfolioRiskScore}/100
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Portfolio Risk Score
            </Typography>
          </Box>
        </Paper>
      </Grid>
    );
  };

  const renderOpportunities = () => {
    if (!marketInsights?.topOpportunities) return null;

    return (
      <Grid item xs={12} md={6}>
        <Card 
          elevation={3}
          sx={{
            height: '100%',
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
            border: '1px solid rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
            }
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box sx={{ 
                width: 6, 
                height: 28, 
                background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                borderRadius: '3px',
                mr: 2 
              }} />
              <TrendingUp sx={{ mr: 1, color: theme.palette.success.main, fontSize: 24 }} />
              <Typography variant="h6" fontWeight="600">Top Opportunities</Typography>
            </Box>
            <List sx={{ py: 0 }}>
              {marketInsights.topOpportunities.map((opportunity, index) => (
                <ListItem 
                  key={index}
                  sx={{ 
                    px: 0,
                    py: 1.5,
                    '&:not(:last-child)': {
                      borderBottom: '1px solid #f0f0f0'
                    }
                  }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ 
                      bgcolor: `${theme.palette.success.main}15`,
                      color: theme.palette.success.main,
                      width: 40,
                      height: 40
                    }}>
                      <CheckCircle />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText 
                    primary={opportunity}
                    primaryTypographyProps={{
                      fontWeight: 500,
                      color: 'text.primary'
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  const renderRisks = () => {
    if (!marketInsights?.risks) return null;

    return (
      <Grid item xs={12} md={6}>
        <Card 
          elevation={3}
          sx={{
            height: '100%',
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
            border: '1px solid rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
            }
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box sx={{ 
                width: 6, 
                height: 28, 
                background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
                borderRadius: '3px',
                mr: 2 
              }} />
              <Warning sx={{ mr: 1, color: theme.palette.warning.main, fontSize: 24 }} />
              <Typography variant="h6" fontWeight="600">Market Risks</Typography>
            </Box>
            <List sx={{ py: 0 }}>
              {marketInsights.risks.map((risk, index) => (
                <ListItem 
                  key={index}
                  sx={{ 
                    px: 0,
                    py: 1.5,
                    '&:not(:last-child)': {
                      borderBottom: '1px solid #f0f0f0'
                    }
                  }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ 
                      bgcolor: `${theme.palette.warning.main}15`,
                      color: theme.palette.warning.main,
                      width: 40,
                      height: 40
                    }}>
                      <Warning />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText 
                    primary={risk}
                    primaryTypographyProps={{
                      fontWeight: 500,
                      color: 'text.primary'
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  const renderQuickStats = () => {
    const propertiesWithValues = properties.filter(p => p.upset_value && parseFloat(p.upset_value) > 0);
    const avgPrice = propertiesWithValues.reduce((sum, p) => sum + parseFloat(p.upset_value), 0) / propertiesWithValues.length;
    const totalValue = propertiesWithValues.reduce((sum, p) => sum + parseFloat(p.upset_value), 0);
    
    const stats = [
      { 
        label: 'Total Properties', 
        value: properties.length.toLocaleString(), 
        subtext: 'active foreclosure listings',
        color: '#1976d2',
        icon: <House sx={{ fontSize: 20, color: '#1976d2' }} />
      },
      { 
        label: 'Average Upset Value', 
        value: formatCurrency(avgPrice || 0), 
        subtext: `across ${propertiesWithValues.length} valued properties`,
        color: '#ed6c02',
        icon: <MonetizationOn sx={{ fontSize: 20, color: '#ed6c02' }} />
      },
      { 
        label: 'Total Portfolio Value', 
        value: formatCurrency(totalValue), 
        subtext: 'estimated market value',
        color: '#2e7d32',
        icon: <Assessment sx={{ fontSize: 20, color: '#2e7d32' }} />
      },
      { 
        label: 'Geographic Coverage', 
        value: `${new Set(properties.map(p => p.county)).size} Counties`, 
        subtext: `spanning ${new Set(properties.map(p => p.city)).size} cities statewide`,
        color: '#9c27b0',
        icon: <LocationOn sx={{ fontSize: 20, color: '#9c27b0' }} />
      }
    ];

    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Paper 
              elevation={1}
              sx={{
                p: 2.5,
                height: 120,
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                '&:hover': {
                  boxShadow: 2
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  fontWeight="500"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '70%'
                  }}
                >
                  {stat.label}
                </Typography>
                {stat.icon}
              </Box>
              <Box>
                <Typography 
                  variant="h6" 
                  fontWeight="700"
                  color={stat.color}
                  sx={{ 
                    mb: 0.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {stat.value}
                </Typography>
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'block'
                  }}
                >
                  {stat.subtext}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderSimpleData = () => {
    // County counts
    const countyCounts = {};
    properties.forEach(property => {
      const county = (property.county || 'Unknown').replace(' County', '');
      countyCounts[county] = (countyCounts[county] || 0) + 1;
    });
    const topCounties = Object.entries(countyCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 6);

    // City counts  
    const cityCounts = {};
    properties.forEach(property => {
      const city = property.city || 'Unknown';
      cityCounts[city] = (cityCounts[city] || 0) + 1;
    });
    const topCities = Object.entries(cityCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 6);

    // Status counts
    const statusCounts = {};
    properties.forEach(property => {
      const status = property.status || 'Unknown';
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    });

    // Price ranges
    const propertiesWithValues = properties.filter(p => p.upset_value && parseFloat(p.upset_value) > 0);
    const priceRanges = {
      'Under $100K': propertiesWithValues.filter(p => parseFloat(p.upset_value) < 100000).length,
      '$100K - $300K': propertiesWithValues.filter(p => parseFloat(p.upset_value) >= 100000 && parseFloat(p.upset_value) < 300000).length,
      '$300K - $500K': propertiesWithValues.filter(p => parseFloat(p.upset_value) >= 300000 && parseFloat(p.upset_value) < 500000).length,
      '$500K+': propertiesWithValues.filter(p => parseFloat(p.upset_value) >= 500000).length,
    };

    // Attorney counts
    const attorneyCounts = {};
    properties.forEach(property => {
      const attorney = property.attorney || 'Unknown';
      attorneyCounts[attorney] = (attorneyCounts[attorney] || 0) + 1;
    });
    const topAttorneys = Object.entries(attorneyCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 6);

    return (
      <Grid container spacing={2}>
        {/* Top Counties */}
        <Grid item xs={12} md={6}>
          <Paper 
            elevation={1} 
            sx={{ 
              p: 2.5, 
              borderRadius: 2, 
              border: '1px solid #e0e0e0', 
              height: 350,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Typography variant="h6" fontWeight="600" gutterBottom>
              Top Counties
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Properties by county
            </Typography>
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {topCounties.map(([county, count], index) => (
                <Box 
                  key={county} 
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    py: 1.5, 
                    borderBottom: index < topCounties.length - 1 ? '1px solid #f0f0f0' : 'none' 
                  }}
                >
                  <Typography 
                    variant="body1" 
                    fontWeight="500"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '70%'
                    }}
                  >
                    {county}
                  </Typography>
                  <Chip 
                    label={count.toLocaleString()} 
                    size="small" 
                    sx={{ 
                      bgcolor: '#f5f5f5', 
                      color: '#1976d2', 
                      fontWeight: '600',
                      minWidth: '60px'
                    }} 
                  />
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Top Cities */}
        <Grid item xs={12} md={6}>
          <Paper 
            elevation={1} 
            sx={{ 
              p: 2.5, 
              borderRadius: 2, 
              border: '1px solid #e0e0e0', 
              height: 350,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Typography variant="h6" fontWeight="600" gutterBottom>
              Top Cities
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Properties by city
            </Typography>
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {topCities.map(([city, count], index) => (
                <Box 
                  key={city} 
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    py: 1.5, 
                    borderBottom: index < topCities.length - 1 ? '1px solid #f0f0f0' : 'none' 
                  }}
                >
                  <Typography 
                    variant="body1" 
                    fontWeight="500"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '70%'
                    }}
                  >
                    {city}
                  </Typography>
                  <Chip 
                    label={count.toLocaleString()} 
                    size="small" 
                    sx={{ 
                      bgcolor: '#f5f5f5', 
                      color: '#ed6c02', 
                      fontWeight: '600',
                      minWidth: '60px'
                    }} 
                  />
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Property Status */}
        <Grid item xs={12} md={6}>
          <Paper 
            elevation={1} 
            sx={{ 
              p: 2.5, 
              borderRadius: 2, 
              border: '1px solid #e0e0e0', 
              height: 350,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Typography variant="h6" fontWeight="600" gutterBottom>
              Property Status
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Current status breakdown
            </Typography>
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {Object.entries(statusCounts).map(([status, count], index) => (
                <Box 
                  key={status} 
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    py: 1.5, 
                    borderBottom: index < Object.keys(statusCounts).length - 1 ? '1px solid #f0f0f0' : 'none' 
                  }}
                >
                  <Typography 
                    variant="body1" 
                    fontWeight="500"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '70%'
                    }}
                  >
                    {status}
                  </Typography>
                  <Chip 
                    label={count.toLocaleString()} 
                    size="small" 
                    sx={{ 
                      bgcolor: '#f5f5f5', 
                      color: '#2e7d32', 
                      fontWeight: '600',
                      minWidth: '60px'
                    }} 
                  />
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Price Ranges */}
        <Grid item xs={12} md={6}>
          <Paper 
            elevation={1} 
            sx={{ 
              p: 2.5, 
              borderRadius: 2, 
              border: '1px solid #e0e0e0', 
              height: 350,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Typography variant="h6" fontWeight="600" gutterBottom>
              Price Ranges
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Value distribution ({propertiesWithValues.length} properties)
            </Typography>
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {Object.entries(priceRanges).map(([range, count], index) => (
                <Box 
                  key={range} 
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    py: 1.5, 
                    borderBottom: index < Object.keys(priceRanges).length - 1 ? '1px solid #f0f0f0' : 'none' 
                  }}
                >
                  <Typography 
                    variant="body1" 
                    fontWeight="500"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '60%'
                    }}
                  >
                    {range}
                  </Typography>
                  <Chip 
                    label={count.toLocaleString()} 
                    size="small" 
                    sx={{ 
                      bgcolor: '#f5f5f5', 
                      color: '#9c27b0', 
                      fontWeight: '600',
                      minWidth: '60px'
                    }} 
                  />
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Top Attorneys */}
        <Grid item xs={12}>
          <Paper 
            elevation={1} 
            sx={{ 
              p: 2.5, 
              borderRadius: 2, 
              border: '1px solid #e0e0e0',
              minHeight: 250
            }}
          >
            <Typography variant="h6" fontWeight="600" gutterBottom>
              Top Attorney Firms
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Most active legal representatives
            </Typography>
            <Grid container spacing={2}>
              {topAttorneys.map(([attorney, count], index) => (
                <Grid item xs={12} sm={6} md={4} key={attorney}>
                  <Box sx={{ 
                    p: 2, 
                    border: '1px solid #f0f0f0', 
                    borderRadius: 2, 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    bgcolor: '#fafafa',
                    height: 60
                  }}>
                    <Typography 
                      variant="body2" 
                      fontWeight="500" 
                      sx={{ 
                        maxWidth: '70%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {attorney.length > 30 ? attorney.substring(0, 30) + '...' : attorney}
                    </Typography>
                    <Chip 
                      label={count} 
                      size="small" 
                      sx={{ 
                        bgcolor: '#fff', 
                        color: '#d32f2f', 
                        fontWeight: '600',
                        minWidth: '50px'
                      }} 
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading property analytics...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        p: 3,
        borderRadius: 2,
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
        border: '1px solid rgba(0,0,0,0.08)'
      }}>
        <Box>
          <Typography variant="h4" component="h2" fontWeight="700" gutterBottom color="primary">
            Property Analytics
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
            Comprehensive analysis of {properties.length.toLocaleString()} foreclosure properties
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          startIcon={aiLoading ? <CircularProgress size={20} color="inherit" /> : <AutoAwesome />}
          onClick={generateAIInsights}
          disabled={aiLoading || !properties.length}
          sx={{ 
            background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
            boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
            px: 3,
            py: 1.5,
            fontSize: '1rem',
            fontWeight: 600,
            borderRadius: 2,
            '&:hover': {
              background: 'linear-gradient(45deg, #5a67d8 0%, #6b46c1 100%)',
              boxShadow: '0 8px 25px rgba(102, 126, 234, 0.6)',
              transform: 'translateY(-2px)'
            }
          }}
        >
          {aiLoading ? 'Generating Insights...' : 'Generate AI Insights'}
        </Button>
      </Box>

      {properties.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No property data available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Property analytics will be displayed once data is loaded.
          </Typography>
        </Box>
      ) : (
        <Box>
          {renderQuickStats()}
          {renderSimpleData()}
        </Box>
      )}
    </Box>
  );
};

export default PropertyAnalytics; 